import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal, Search, CircleCheck } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { useConfig } from "@/lib/auth";
import { FUELS, GEARS, BODIES } from "@/lib/format";
import { VehicleGrid } from "@/components/xd/VehicleCard";
import { Empty, Loader, Field } from "@/components/xd/ui";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";

const ALL = "__all";
const KEYS = ["q", "brand", "model", "fuel", "gearbox", "body_type", "color", "category", "year_min", "year_max", "price_min", "price_max", "mileage_max", "sort", "view"];

const Pick = ({ id, label, value, options, onChange }) => {
  const { t } = useLang();
  return (
    <Field label={label}>
      <Select value={value || ALL} onValueChange={(v) => onChange(v === ALL ? "" : v)}>
        <SelectTrigger className="xd-input" data-testid={`filter-${id}`}><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>{t("filter.all")}</SelectItem>
          {options.map((o) => <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </Field>
  );
};

export const FilterPanel = ({ f, set, facets }) => {
  const { t, lang } = useLang();
  const { categories } = useConfig();
  const opt = (arr, prefix) => arr.map((x) => ({ value: x, label: prefix ? t(`${prefix}.${x}`) : x }));
  const num = (k, ph) => (
    <Input type="number" inputMode="numeric" dir="ltr" placeholder={ph} className="xd-input" value={f[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`filter-${k}`} />
  );
  return (
    <div className="space-y-5">
      <Pick id="category" label={t("filter.category")} value={f.category} onChange={(v) => set("category", v)} options={categories.map((c) => ({ value: c.id, label: lang === "ar" ? c.name_ar : c.name_he }))} />
      <Pick id="brand" label={t("spec.brand")} value={f.brand} onChange={(v) => set("brand", v)} options={opt(facets.brand || [])} />
      <Pick id="model" label={t("spec.model")} value={f.model} onChange={(v) => set("model", v)} options={opt(facets.model || [])} />
      <div className="grid grid-cols-2 gap-3"><Field label={t("filter.year_min")}>{num("year_min", "2018")}</Field><Field label={t("filter.year_max")}>{num("year_max", "2025")}</Field></div>
      <div className="grid grid-cols-2 gap-3"><Field label={t("filter.price_min")}>{num("price_min", "₪")}</Field><Field label={t("filter.price_max")}>{num("price_max", "₪")}</Field></div>
      <Field label={t("filter.mileage_max")}>{num("mileage_max", t("unit.km"))}</Field>
      <Pick id="fuel" label={t("spec.fuel")} value={f.fuel} onChange={(v) => set("fuel", v)} options={opt(FUELS, "fuel")} />
      <Pick id="gearbox" label={t("spec.gearbox")} value={f.gearbox} onChange={(v) => set("gearbox", v)} options={opt(GEARS, "gear")} />
      <Pick id="body_type" label={t("spec.body_type")} value={f.body_type} onChange={(v) => set("body_type", v)} options={opt(BODIES, "body")} />
      <Pick id="color" label={t("spec.color")} value={f.color} onChange={(v) => set("color", v)} options={opt(facets.color || [])} />
    </div>
  );
};

export const useFilters = () => {
  const [sp, setSp] = useSearchParams();
  const f = useMemo(() => Object.fromEntries(KEYS.map((k) => [k, sp.get(k) || ""])), [sp]);
  const set = (k, v) => {
    const n = new URLSearchParams(sp);
    if (v) n.set(k, v); else n.delete(k);
    setSp(n, { replace: true });
  };
  const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
  return { f, set, params, reset: () => setSp({}, { replace: true }) };
};

const ALL_BRANDS = "__all";

const BrandView = ({ value, onChange, url = "/public/available-brands", labelKey = "inventory.view_available", allKey = "inventory.all_available" }) => {
  const { t, lang } = useLang();
  const [d, setD] = useState(null);
  useEffect(() => { api.get(url).then((r) => setD(r.data)).catch(() => setD({ total: 0, items: [] })); }, [url]);
  const items = d?.items || [];
  const current = value && !items.some((b) => b.brand === value) && d ? [{ brand: value, count: 0 }] : [];
  return (
    <Select dir={lang === "en" ? "ltr" : "rtl"} value={value || ALL_BRANDS} onValueChange={(v) => onChange(v === ALL_BRANDS ? "" : v)}>
      <SelectTrigger className="xd-input w-full sm:w-auto sm:min-w-[15rem] sm:max-w-[26rem] gap-2 [&>span]:truncate [&>span]:min-w-0" data-testid="inventory-brand-select">
        <CircleCheck size={15} className="text-emerald-400 shrink-0" /><span className="text-zinc-400 shrink-0">{t(labelKey)}:</span><span className="sr-only"><SelectValue /></span><span className="truncate min-w-0">{value ? <><span dir="ltr">{value}</span> <span className="ltr-num text-zinc-500">({[...items, ...current].find((b) => b.brand === value)?.count ?? 0})</span></> : <>{t(allKey)} <span className="ltr-num text-zinc-500">({d?.total ?? "…"})</span></>}</span>
      </SelectTrigger>
      <SelectContent className="max-h-[60vh]">
        <SelectItem value={ALL_BRANDS} data-testid="brand-option-all">{t(allKey)} <span className="ltr-num text-zinc-500">({d?.total ?? "…"})</span></SelectItem>
        {[...items, ...current].map((b) => (
          <SelectItem key={b.brand} value={b.brand} data-testid={`brand-option-${b.brand}`}>
            <span dir="ltr">{b.brand}</span>{b.he && b.he !== b.brand && <span className="text-zinc-400"> — {b.he} — {b.ar}</span>} <span className="ltr-num text-zinc-500">({b.count})</span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

export const FilterBar = ({ f, set, facets, reset, count, availabilityView = false, brandProps, sorts = ["newest", "price_asc", "price_desc", "year_desc", "mileage_asc"] }) => {
  const { t } = useLang();
  return (
    <div className="flex flex-col sm:flex-row gap-3 mb-8">
      <div className="relative flex-1">
        <Search size={16} className="absolute top-1/2 -translate-y-1/2 start-4 text-zinc-500" />
        <Input className="xd-input !ps-11" placeholder={t("filter.search")} value={f.q} onChange={(e) => set("q", e.target.value)} data-testid="inventory-search-input" />
      </div>
      <div className="flex gap-3">
        {availabilityView ? <BrandView value={f.brand} onChange={(v) => set("brand", v)} {...brandProps} /> : (
          <Select value={f.sort || sorts[0]} onValueChange={(v) => set("sort", v)}>
            <SelectTrigger className="xd-input w-44" data-testid="inventory-sort"><SelectValue /></SelectTrigger>
            <SelectContent>{sorts.map((s) => <SelectItem key={s} value={s}>{t(`sort.${s}`)}</SelectItem>)}</SelectContent>
          </Select>
        )}
        {!availabilityView && <Sheet>
          <SheetTrigger asChild>
            <button className="btn-ghost" data-testid="open-filters-button"><SlidersHorizontal size={16} />{t("filter.title")}</button>
          </SheetTrigger>
          <SheetContent side="right" className="bg-black border-white/10 overflow-y-auto w-[90vw] sm:max-w-md" data-testid="filters-sheet">
            <SheetHeader><SheetTitle className="text-white text-start">{t("filter.title")}</SheetTitle></SheetHeader>
            <div className="mt-6"><FilterPanel f={f} set={set} facets={facets} /></div>
            <div className="mt-8 flex items-center justify-between">
              <span className="text-sm text-zinc-400">{t("filter.results", { n: count })}</span>
              <button className="text-sm text-zinc-400 hover:text-white underline" onClick={reset} data-testid="filters-reset">{t("filter.reset")}</button>
            </div>
          </SheetContent>
        </Sheet>}
      </div>
    </div>
  );
};

export default function Inventory() {
  const { t } = useLang();
  const { f, set, params, reset } = useFilters();
  const [data, setData] = useState(null);
  const { view, sort, ...rest } = params;
  const key = JSON.stringify({ ...rest, availability: "available" });
  useEffect(() => {
    const id = setTimeout(() => {
      api.get("/public/vehicles", { params: JSON.parse(key) }).then((r) => setData(r.data)).catch(() => setData({ items: [], facets: {} }));
    }, 250);
    return () => { clearTimeout(id); };
  }, [key]);
  return (
    <div className="mx-auto max-w-7xl px-4 md:px-8 py-10 md:py-16">
      <p className="eyebrow">{t("inventory.eyebrow")}</p>
      <h1 className="mt-3 mb-8 text-4xl sm:text-5xl font-semibold tracking-tight chrome-text">{t("nav.inventory")}</h1>
      <FilterBar f={f} set={set} facets={data?.facets || {}} reset={reset} count={data?.items.length ?? 0} availabilityView />
      {data && <p className="text-sm text-zinc-500 -mt-4 mb-6" data-testid="inventory-result-count">{t(`inventory.count_available${data.items.length === 1 ? "_one" : ""}`, { n: data.items.length })}</p>}
      {!data ? <Loader /> : data.items.length ? <VehicleGrid items={data.items} /> : <Empty text={t("inventory.empty")} />}
    </div>
  );
}
